import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography } from '../theme';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { useThemedStyles } from '../hooks/useThemedStyles';

const HIT_SLOP = { top: 8, bottom: 8, left: 8, right: 8 };

/**
 * Small icon-only button matching this header's chrome — a plain
 * TouchableOpacity+Ionicons pair, factored out so every pane header (the
 * contact profile's own top bar, and the conversation/call-history panes
 * next to it) uses the exact same hit target and icon sizing instead of each
 * screen inventing its own slightly-different close/back button.
 */
export const PaneHeaderIconButton: React.FC<{
  icon: React.ComponentProps<typeof Ionicons>['name'];
  onPress?: () => void;
  color?: string;
  size?: number;
  disabled?: boolean;
}> = ({ icon, onPress, color, size = 22, disabled }) => {
  const styles = useThemedStyles(getStyles);
  return (
    <TouchableOpacity style={styles.iconButton} onPress={onPress} disabled={disabled} hitSlop={HIT_SLOP}>
      <Ionicons name={icon} size={size} color={(color || colors.text.secondary) as string} />
    </TouchableOpacity>
  );
};

export interface PaneHeaderProps {
  /** Rendered at the row's start — typically a close (X) or back (chevron)
   * PaneHeaderIconButton. Omit for a bare leading spacer. */
  leading?: React.ReactNode;
  /** Centered/flexible title text — omit to let `trailing` sit flush to the
   * end instead (matches how the right pane's close-only header looks
   * today: no title, just one icon at the end). */
  title?: string;
  titleContent?: React.ReactNode;
  /** Rendered at the row's end — action icons (quick actions, save, delete,
   * call-back, etc.). */
  trailing?: React.ReactNode;
}

/**
 * Shared header bar for the contact profile (middle pane) and whatever's
 * currently embedded in the right pane (conversation, call history) — same
 * padding/border/icon treatment in both places instead of each pane content
 * inventing its own ad-hoc row. Deliberately NOT FormHeader.tsx: that
 * component only positions correctly when rendered through ScreenLayout's
 * own `formHeader` slot (see its doc comment), which the middle and right
 * panes can't both use at once — they're separate sibling trees under
 * ScreenLayout, not one shared slot. This is a plain, pane-safe row instead.
 */
export const PaneHeader: React.FC<PaneHeaderProps> = ({ leading, title, titleContent, trailing }) => {
  const styles = useThemedStyles(getStyles);
  const rtl = useRTLStyles();
  return (
    <View style={[styles.row, { flexDirection: rtl.rowDirection }]}>
      {leading || <View style={styles.sideSpacer} />}
      {titleContent ? (
        <View style={styles.titleContent}>{titleContent}</View>
      ) : !!title && (
        <Text style={[styles.title, { textAlign: rtl.textStart }]} numberOfLines={1}>{title}</Text>
      )}
      <View style={[styles.trailing, { flexDirection: rtl.rowDirection }]}>{trailing}</View>
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  row: {
    alignItems: 'center',
    // Exactly 2-3 children (leading-or-spacer, optional title, trailing) —
    // space-between pushes leading/trailing to opposite edges whether or not
    // a title sits between them, with no flex-ratio guessing needed.
    justifyContent: 'space-between',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  // Fixed width (matches iconButton's own) so a centered title stays
  // genuinely centered even when only one side has a real button.
  sideSpacer: {
    width: 32,
  },
  title: {
    flex: 1,
    marginHorizontal: spacing.sm,
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
    color: colors.textDark,
  },
  titleContent: {
    flex: 1,
    minWidth: 0,
    marginHorizontal: spacing.sm,
  },
  trailing: {
    alignItems: 'center',
    gap: spacing.sm,
  },
  iconButton: {
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
